<template>
  <div
    class="bottom-panel-padding"
  >
    <h4>Landing page URL</h4>
    <b-form-group
      id="url-input-fieldset"
      label-for="url-input"
    >
      <b-row class="gutters-xs">
        <b-col
          cols="11"
        >
          <b-form-input
            id="url-input"
            :value="contentItem.url"
            trim
            data-test="content-copy-panel-url"
            disabled
            :state="isValidContentType"
          />
          <b-form-invalid-feedback
            id="input-live-feedback"
            data-test="url-feedback"
          >
            <span
              variant="primary"
              class="ml-1 border-0"
              data-test="invalid-tooltip"
              v-b-tooltip.hover
              title="Landing page URLs can not lead to a file. Examples: images, video, audio, documents"
            >
              Invalid
              <i class="fal fa-exclamation-circle" />
            </span>
          </b-form-invalid-feedback>
        </b-col>
        <b-col
          cols="1"
        >
          <a
            class="btn btn-link"
            :href="contentItem.url"
            target="_blank"
          >
            <i
              class="fal fa-external-link"
            />
          </a>
        </b-col>
      </b-row>
    </b-form-group>
    <h4>Advert content</h4>
    <b-form-group
      id="title-input-fieldset"
    >
      <template
        slot="label"
        label-for="title-input"
        class="form-label"
      >
        <span class="form-label">Title
          <span class="form-label-small text-muted">
            {{ getLength(newTitle) }}/{{ maxTitleLength }}
          </span>
        </span>
      </template>
      <b-form-input
        id="title-input"
        v-model="titleProp"
        @click="onClickCopy"
        @blur.native="onBlurTitle"
        @keyup.enter="$event.target.blur()"
        :maxlength="maxTitleLength"
        trim
        data-test="content-copy-panel-title"
        :disabled="isReadOnly('content-items')"
      />
    </b-form-group>
    <b-form-group
      id="content-input-fieldset"
    >
      <template
        slot="label"
        label-for="content-input"
        class="form-label"
      >
        <span class="form-label">Content
          <span class="form-label-small text-muted">
            {{ getLength(newContent) }}/{{ maxContentLength }}
          </span>
        </span>
      </template>
      <b-form-textarea
        id="content-input"
        v-model="contentProp"
        @click="onClickCopy"
        @blur.native="onBlurContent"
        @keydown.enter.exact.prevent
        @keyup.enter.exact="$event.target.blur()"
        @keydown.enter.shift.exact="newline"
        placeholder=""
        :maxlength="maxContentLength"
        rows="3"
        max-rows="6"
        data-test="content-copy-panel-content"
        :disabled="isReadOnly('content-items')"
        :key="componentKey"
      />
      <small class="form-text text-muted mt-3">
        To add a newline
        <kbd class="primary ml-3">Shift + Enter</kbd></small>
    </b-form-group>
    <b-form-group
      id="call-to-action-input-fieldset"
    >
      <template
        slot="label"
        label-for="call-to-action-input"
        class="form-label"
      >
        <span class="form-label">Call to action</span>
      </template>
      <b-form-input
        id="call-to-action-input"
        v-model="ctaProp"
        @click="onClickCopy"
        @blur.native="onBlurCta"
        @keyup.enter="$event.target.blur()"
        placeholder="Read more"
        data-test="content-copy-panel-cta"
        :disabled="isReadOnly('content-items')"
      />
    </b-form-group>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import helper from '@/core/helper';
import suppressibleBox from '@/core/mixins/suppressibleBox';

export default {
  name: 'CopyPanel',
  mixins: [
    suppressibleBox,
  ],
  props: {
    readOnly: {
      type: Array,
      default: () => [],
    },
    contentItemProperties: {
      type: Object,
      default: () => {},
    },
    contentItem: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      itemId: 0,
      newTitle: '',
      newContent: '',
      newCTA: null,
      maxContentLength: 30000,
      maxTitleLength: 254,
      componentKey: 0,
    };
  },
  computed: {
    ...mapGetters('campaigns', {
      isCampaignReadonly: types.GET_IS_CAMPAIGN_READONLY,
      isManagedCampaign: types.GET_IS_MANAGED_CAMPAIGN,
    }),
    titleProp: {
      get() { return this.contentItem.title; },
      set(val) { this.newTitle = val; },
    },
    contentProp: {
      get() {
        return this.contentItem.content ? this.contentItem.content.replace(/<br\s*[/]?>/gi, '\n')
          : '';
      },
      set(val) { this.newContent = val; },
    },
    ctaProp: {
      get() {
        return this.contentEditableProperties.includes('alternateCTA')
          ? this.contentItem.alternateCTA
          : this.contentItem.cta;
      },
      set(val) { this.newCTA = val; },
    },
    isValidContentType() {
      if (!this.contentItemProperties) return true;
      if ('contentType' in this.contentItemProperties) {
        const { contentType } = this.contentItemProperties;
        if (!contentType.includes('html')) {
          return false;
        }
      }
      return true;
    },
    contentEditableProperties() {
      return Object.keys(this.contentItem);
    },
  },
  methods: {
    updateContent: helper.debounce(function updateContentItem(data) {
      this.$emit('updateContentItem', data);
    }, 500),
    onClickCopy() {
      if (this.state === 'PUBLISHED' || this.state === 'PAUSED' || this.state === 'APPROVAL') {
        this.boxOk(
          'Warning',
          'Editing this content item will move it to the DRAFT state and you will need to submit the updated item for approval. If the content item is currently live, then this will stop it from serving.',
          'suppressEditNonDraftContentItemDialog',
        ).then(() => true);
      }
    },
    onBlurTitle() {
      this.onBlurContentItemPresent('title', this.itemId, this.titleProp, this.newTitle);
    },
    onBlurContent() {
      const { newContent } = this;
      this.onBlurContentItemPresent('content', this.itemId, this.contentProp, newContent);
    },
    onBlurCta() {
      if (this.newCTA !== '') {
        if (this.contentEditableProperties.includes('cta')) {
          this.onBlurContentItemPresent('cta', this.itemId, this.ctaProp, this.newCTA);
        } else {
          this.onBlurContentItemPresent('alternateCTA', this.itemId, this.ctaProp, this.newCTA);
        }
      }
    },
    onBlurContentItemPresent(propertyName, itemId, existingProperty, newProperty) {
      if (existingProperty !== newProperty && newProperty) {
        this.updateContent({
          body: { [propertyName]: newProperty },
          contentItem: this.contentItem,
        });
        this.resetData();
      }
    },
    resetData() {
      this.newTitle = '';
      this.newContent = '';
      this.newCTA = null;
    },
    setContent() {
      this.itemId = this.contentItem.id;
      this.newTitle = this.contentItem.title;
      this.newContent = this.contentItem.content;
      this.newCTA = this.contentEditableProperties.includes('alternateCTA')
        ? this.contentItem.alternateCTA
        : this.contentItem.cta;
      this.componentKey++;
    },
    isReadOnly(property) {
      return this.readOnly.includes(property) || this.isCampaignReadonly;
    },
    newline() {
      this.contentProp = `${this.contentProp}\n`;
    },
    getLength(text) {
      return text ? text.length : 0;
    },
  },
  mounted() {
    this.setContent();
  },
};
</script>
